import CloseIcon from '@mui/icons-material/Close';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { ErrorContainer } from '@openx/components-core/lib/CreativeMediaError/ErrorContainer.tsx';
import { useTranslation } from 'react-i18next';

import { CREATIVE_SANDBOX_URL } from '../../../../config.ts';

const SANDBOX_ORIGIN = new URL(CREATIVE_SANDBOX_URL).origin;

type PreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  markup: string;
  isError: boolean;
  width: number;
  height: number;
};

export const PreviewModal = ({ isOpen, onClose, markup, isError, width, height }: PreviewModalProps) => {
  const { t } = useTranslation();

  const maxFullscreenWidth = window.innerWidth * 0.8;
  const maxFullscreenHeight = window.innerHeight * 0.8;
  const scale = Math.min(maxFullscreenWidth / width, maxFullscreenHeight / height, 1);
  const fullscreenWidth = Math.round(width * scale);
  const fullscreenHeight = Math.round(height * scale);

  return (
    <>
      <Backdrop open={isOpen} onClick={onClose} sx={{ zIndex: theme => theme.zIndex.modal }} />

      {isOpen && (
        <>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: fullscreenWidth,
              height: fullscreenHeight,
              zIndex: theme => theme.zIndex.modal + 1,
              outline: 'none',
              overflow: 'hidden',
            }}
            data-test="media-preview-size"
          >
            {isError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ErrorContainer height={fullscreenHeight} width={fullscreenWidth} />
              </Box>
            )}

            <iframe
              src={CREATIVE_SANDBOX_URL}
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
              width={fullscreenWidth}
              height={fullscreenHeight}
              style={{ border: 0, display: 'block', background: 'transparent' }}
              title={t('Creative preview')}
              data-test="live-ad"
              onLoad={e => {
                const frame = e.currentTarget;
                if (markup && frame.contentWindow) {
                  frame.contentWindow.postMessage(JSON.stringify({ type: 'render', html: markup }), SANDBOX_ORIGIN);
                }
              }}
            />
          </Box>

          <IconButton
            aria-label={t('Close creative preview')}
            onClick={onClose}
            sx={theme => ({
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: `translate(calc(-50% + ${fullscreenWidth / 2}px + 8px), calc(-50% - ${fullscreenHeight / 2}px - 8px))`,
              zIndex: theme.zIndex.modal + 2,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 2,
              '&:hover': { backgroundColor: theme.palette.action.hover },
            })}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </>
  );
};
